/** Pending and failed local writes must be acknowledged before this document can end. */
export class PersistenceGate {
    private pending = 0;
    private sequence = 0;
    private resources = new Map<string, {sequence: number; failed: boolean}>();

    isSafe(): boolean { return this.pending === 0 && ![...this.resources.values()].some(value => value.failed); }

    async write<T>(keys: string | string[], operation: () => Promise<T>,
        acknowledged: (value: T) => boolean = value => !(value && typeof value === 'object' && 'ok' in value && value.ok === false),
        covers?: (key: string) => boolean): Promise<T> {
        const sequence = ++this.sequence;
        const affected = new Set(typeof keys === 'string' ? [keys] : keys);
        // Include earlier in-flight writes as well as failures; a delete can cover
        // an autosave whose failed write never created a physical row.
        if (covers) for (const key of this.resources.keys()) if (covers(key)) affected.add(key);
        for (const key of affected) if (!this.resources.has(key)) this.resources.set(key, {sequence: 0, failed: false});
        this.pending++;
        const complete = (failed: boolean) => {
            for (const key of affected) {
                if ((this.resources.get(key)?.sequence ?? 0) <= sequence) this.resources.set(key, {sequence, failed});
            }
        };
        try {
            const value = await operation();
            complete(!acknowledged(value));
            return value;
        } catch (error) {
            complete(true);
            throw error;
        } finally {
            this.pending--;
            // Once all older operations settled, successful watermarks are no longer needed.
            if (!this.pending) for (const [key, value] of this.resources) if (!value.failed) this.resources.delete(key);
        }
    }
}

export const persistenceGate = new PersistenceGate();

const writes = new Set(['updateStorage', 'cacheItem', 'uncacheItem', 'uncacheDependents', 'uncacheByEntityId']);

/** The worker serializes autosave writes/deletes; acknowledgement follows that disk order. */
export function trackStorageWrites<T extends object>(worker: T, gate = persistenceGate): T {
    return new Proxy(worker, {get(target, property) {
        const method = Reflect.get(target, property) as unknown;
        if (typeof property !== 'string' || !writes.has(property) || typeof method !== 'function') return method;
        return (...args: unknown[]) => {
            const invoke = () => Reflect.apply(method, target, args) as Promise<unknown>;
            const prefix = JSON.stringify(args.slice(0, 2));
            if (property === 'updateStorage') {
                const store = prefix + ':backup:' + String(args[3]) + ':';
                const rows = (args[5] ?? args[4]) as {id?: string}[] | null;
                const keys = rows?.map(row => store + row.id) ?? [];
                if (args[5] == null) keys.push(store + '*');
                return gate.write(keys, invoke, undefined,
                    args[5] == null ? key => key.startsWith(store) : undefined);
            }
            const key = prefix + ':autosave:' + JSON.stringify(property === 'uncacheByEntityId'
                ? [args[2], '*', args[3], 'delete-by-id'] : property === 'uncacheDependents'
                    ? [...args.slice(2, 5), 'delete-dependents'] : args.slice(2, 5));
            const matches = (key: string) => {
                const start = prefix + ':autosave:';
                if (!key.startsWith(start)) return false;
                const [type, parent, id, scope] = JSON.parse(key.slice(start.length)) as string[];
                // Removing a parent alone does not acknowledge a failed child deletion.
                if (scope) return false;
                return property === 'uncacheByEntityId' ? type === args[2] && id === args[3] :
                    parent === args[4] || (type === args[2] && parent === args[3] && id === args[4]);
            };
            return gate.write(
                key,
                invoke, undefined,
                property === 'uncacheByEntityId' || property === 'uncacheDependents' ? matches : undefined,
            );
        };
    }});
}
